import PeopleIcon from "@mui/icons-material/People";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TuneIcon from "@mui/icons-material/Tune";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Paper, Stack } from "@mui/material";
import { type FC, type ReactElement, type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemTabs } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import { BusyOverlay } from "../../../components/UIToolkit/molecules/BusyOverlay";
import { SystemPageHeader } from "../../../components/UIToolkit/molecules/SystemPageHeader";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { ImportManualHoursDialog } from "../AuditTab/ImportManualHoursDialog";
import { ImportShiftsDialog } from "../AuditTab/ImportShiftsDialog";
import { PbjDownloadMenu } from "../PbjDownloadMenu";
import { cardShadow, subTabsPillSx } from "../PredictionTab/colors";
import { formatPeriodRange } from "../pbjGridFormat";
import { EmployeesTable } from "./EmployeesTable";
import { StaffingHoursTable } from "./StaffingHoursTable";

type Props = {
	committedStart: string;
	committedEnd: string;
	// True while a page-level recalculation (upsert) is running; dims + freezes the sub-tables.
	isCalculating?: boolean;
	// Wraps a "Change period" header button in the page-level period/facility popover.
	renderChangePeriod: (trigger: ReactElement) => ReactNode;
};

type SubmissionSubTab = "nursing_hours" | "employees";

export const SubmissionSheetsTab: FC<Props> = ({ committedStart, committedEnd, isCalculating = false, renderChangePeriod }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const [subTab, setSubTab] = useState<SubmissionSubTab>("nursing_hours");
	const [manualImportOpen, setManualImportOpen] = useState(false);
	const [shiftImportOpen, setShiftImportOpen] = useState(false);

	const periodRange = formatPeriodRange(committedStart, committedEnd);
	const subtitle = [t("pbj_model.submission.subtitle"), periodRange].filter(Boolean).join(" · ");

	const subTabs = useMemo(
		() => [
			{ label: t("pbj_model.audit.tabs.nursing_hours"), value: "nursing_hours" as const, icon: <ScheduleIcon sx={{ fontSize: 18 }} /> },
			{ label: t("pbj_model.audit.tabs.employees"), value: "employees" as const, icon: <PeopleIcon sx={{ fontSize: 18 }} /> },
		],
		[t],
	);

	return (
		<Stack spacing={2.5} sx={{ height: "100%", minHeight: 0 }} data-testid="pbj-model-submission-tab">
			<SystemPageHeader
				dataTestId="pbj-model-submission-header"
				eyebrow={t("pbj_model.tabs.submission")}
				title={t("pbj_model.submission.title")}
				subtitle={subtitle}
				actions={
					<>
						{renderChangePeriod(
							<SystemButton buttonVariant="secondary" size="small" leadingIcon={<TuneIcon sx={{ fontSize: 16 }} />} noFullWidthMobile>
								{t("pbj_model.hero.change_period")}
							</SystemButton>,
						)}
						<SystemButton
							buttonVariant="secondary"
							size="small"
							leadingIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
							onClick={() => setShiftImportOpen(true)}
							noFullWidthMobile
							data-testid="pbj-model-submission-import-shifts"
						>
							{t("pbj_model.import.button")}
						</SystemButton>
						<SystemButton
							buttonVariant="secondary"
							size="small"
							leadingIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
							onClick={() => setManualImportOpen(true)}
							noFullWidthMobile
							data-testid="pbj-model-submission-import-manual"
						>
							{t("pbj_model.manual_import.button")}
						</SystemButton>
						<PbjDownloadMenu departmentId={departmentId} committedStart={committedStart} committedEnd={committedEnd} />
					</>
				}
			/>
			<Paper
				elevation={0}
				sx={(theme) => ({
					p: { xs: 2, md: 2.5 },
					borderRadius: 3,
					bgcolor: "background.paper",
					boxShadow: cardShadow(theme),
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
				})}
			>
				<SystemTabs<SubmissionSubTab>
					dataTestId="pbj-model-submission-subtabs"
					tabs={subTabs}
					currentTab={subTab}
					onTabChange={setSubTab}
					sx={(theme) => subTabsPillSx(theme)}
				/>
				<BusyOverlay busy={isCalculating} dataTestId="pbj-model-submission-recalculating" fillHeight sx={{ pt: 2.5, flex: 1 }}>
					{subTab === "nursing_hours" && <StaffingHoursTable startDate={committedStart} endDate={committedEnd} />}
					{subTab === "employees" && <EmployeesTable startDate={committedStart} endDate={committedEnd} />}
				</BusyOverlay>
			</Paper>
			<ImportShiftsDialog open={shiftImportOpen} onClose={() => setShiftImportOpen(false)} departmentId={departmentId} />
			<ImportManualHoursDialog open={manualImportOpen} onClose={() => setManualImportOpen(false)} departmentId={departmentId} />
		</Stack>
	);
};
